/**
 * Recording a UPI or cash payment from the console (owner decision 2026-09-15):
 * against an unpaid invoice, or as an advance with no invoice.
 *
 * Pure rules, no I/O, so they are proved in node like the rest of src/domain.
 * They mirror what radpayment enforces on POST .../direct, so a mistake is
 * shown before anything is sent:
 *   - UPI needs its UTR / transaction id; cash may leave the voucher blank
 *     (radpayment generates one);
 *   - the amount is positive with at most two decimals (paise);
 *   - the received moment is not in the future (5 minutes of clock skew) and
 *     not more than 366 days back.
 *
 * 🔑 THE RECEIVED MOMENT IS INDIA TIME, sent with its offset. For UPI the
 * payment time and the receipt time are one moment (owner, 2026-09-14), so the
 * form asks for the bank app's date and time. A date with no time is sent as
 * 12:00:00 IST -- the convention the receipt prints as a date alone.
 */

export type PaymentMethod = "upi" | "cash";

export interface PaymentDraft {
  accountId: string;
  /** null records an advance: money received with no invoice yet. */
  invoiceId: string | null;
  method: PaymentMethod;
  /** UTR / UPI transaction id, or the cash voucher number. */
  reference: string;
  amount: string;
  /** YYYY-MM-DD, India date. */
  receivedDate: string;
  /** HH:MM (24h), India time; "" when not known. */
  receivedTime: string;
}

export const IST_OFFSET = "+05:30";
const IST_OFFSET_MS = 330 * 60 * 1000;
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const MAX_AGE_MS = 366 * 24 * 60 * 60 * 1000;
export const REFERENCE_MAX = 100;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isUuid(s: string): boolean {
  return UUID_RE.test(s.trim());
}

/** Today's date in India, YYYY-MM-DD. */
export function todayIST(now: Date = new Date()): string {
  return new Date(now.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The RFC 3339 moment radpayment takes as `receivedat`, or null when the date
 * is missing or malformed. A real calendar date is required (2026-02-30 is
 * refused, not rolled into March).
 */
export function receivedAtFrom(date: string, time: string): string | null {
  const d = DATE_RE.exec(date.trim());
  if (!d) return null;
  const [, y, m, day] = d;
  const probe = new Date(`${y}-${m}-${day}T00:00:00Z`);
  if (Number.isNaN(probe.getTime()) || probe.toISOString().slice(0, 10) !== `${y}-${m}-${day}`) return null;
  const t = time.trim();
  if (t === "") return `${y}-${m}-${day}T12:00:00${IST_OFFSET}`;
  const tm = TIME_RE.exec(t);
  if (!tm) return null;
  return `${y}-${m}-${day}T${tm[1]}:${tm[2]}:00${IST_OFFSET}`;
}

/** Rupees as a number, or null unless positive with at most two decimals. */
export function parseAmount(s: string): number | null {
  const t = s.trim().replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Everything wrong with the draft, or [] when it may be recorded. */
export function paymentDraftProblems(d: PaymentDraft, now: Date = new Date()): string[] {
  const p: string[] = [];
  if (!isUuid(d.accountId)) p.push("Choose the customer.");
  if (d.invoiceId !== null && !isUuid(d.invoiceId)) p.push("Choose the invoice again.");
  const ref = d.reference.trim();
  if (d.method === "upi" && ref === "") p.push("Enter the UPI transaction id (UTR).");
  if (ref.length > REFERENCE_MAX) p.push(`The reference must be at most ${REFERENCE_MAX} characters.`);
  if (parseAmount(d.amount) === null) p.push("Enter the amount received, in rupees (up to two decimals).");

  const at = receivedAtFrom(d.receivedDate, d.receivedTime);
  if (at === null) {
    p.push(d.receivedDate.trim() === "" ? "Enter the date the money arrived." : "Enter a valid date and time (HH:MM).");
  } else {
    const when = Date.parse(at);
    if (when > now.getTime() + CLOCK_SKEW_MS) p.push("The payment cannot be in the future.");
    if (when < now.getTime() - MAX_AGE_MS) p.push("The payment is more than a year old; record it with support.");
  }
  return p;
}
